import { clsx } from 'clsx';
import React from 'react';
import { BaseComponentProps } from '../../types';

interface InputProps extends BaseComponentProps {
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'search';
  name?: string;
  placeholder?: string;
  value?: string;
  defaultValue?: string;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  label?: string;
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

const Input: React.FC<InputProps> = ({
  type = 'text',
  name,
  placeholder,
  value,
  defaultValue,
  disabled = false,
  required = false,
  error,
  label,
  helperText,
  leftIcon,
  rightIcon,
  onChange,
  onFocus,
  onBlur,
  className = '',
  ...props
}) => {
  const hasError = !!error;
  const hasLeftIcon = !!leftIcon;
  const hasRightIcon = !!rightIcon;

  return (
    <div className={className}>
      {label && (
        <label className='block text-sm font-medium text-gray-700 mb-1'>
          {label}
          {required && <span className='text-red-500 ml-1'>*</span>}
        </label>
      )}

      <div className='relative'>
        {hasLeftIcon && (
          <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
            <div className='h-5 w-5 text-gray-400'>{leftIcon}</div>
          </div>
        )}

        <input
          type={type}
          name={name}
          placeholder={placeholder}
          value={value}
          defaultValue={defaultValue}
          disabled={disabled}
          required={required}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          className={clsx(
            'input-field',
            hasLeftIcon && 'pl-10',
            hasRightIcon && 'pr-10',
            hasError && 'border-red-300 focus:ring-red-500 focus:border-red-500',
            disabled && 'bg-gray-50 cursor-not-allowed'
          )}
          {...props}
        />

        {hasRightIcon && (
          <div className='absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none'>
            <div className='h-5 w-5 text-gray-400'>{rightIcon}</div>
          </div>
        )}
      </div>

      {error && <p className='mt-1 text-sm text-red-600'>{error}</p>}

      {helperText && !error && <p className='mt-1 text-sm text-gray-500'>{helperText}</p>}
    </div>
  );
};

export default Input;
